"use client";

import { useEffect, useId, useRef, useState } from "react";
import { useSiteContext } from "@/components/providers/SiteContextProvider";
import { getTranslations } from "@/lib/i18n";

/**
 * Clamps CMS-authored rich-text HTML (category hero intro) to ~2 lines with
 * an accessible "Lees meer" / "Toon minder" toggle.
 *
 * 2026-08-30 above-the-fold fix (Dennis, regie-sessie, AskUserQuestion-
 * keuze): the category-page hero intro pushed the first offer row below
 * the fold. Clamping it frees vertical space without losing the SEO copy.
 *
 * - Server-rendered collapsed by default (`expanded` starts `false`), so
 *   there is no layout shift on hydration.
 * - The FULL sanitized html is ALWAYS rendered into the DOM -- the clamp
 *   is a pure CSS overflow effect (`line-clamp-2`), never a conditional
 *   omission -- so the complete intro stays indexable for SEO regardless
 *   of collapsed/expanded state or whether JS ran at all.
 * - `aria-expanded` on the toggle reflects the live state; `aria-controls`
 *   points at the content it expands/collapses.
 * - 2026-08-30 regie-fix: the toggle is a dead button when the intro is
 *   already <= 2 lines (nothing to expand). After mount, a ref measures
 *   real overflow (`scrollHeight > clientHeight`) while still clamped and
 *   only then shows the toggle (`clamped` state). Server-render has no
 *   toggle; it appears post-hydration only for genuinely overflowing
 *   intros. Once expanded, the toggle stays visible (`clamped || expanded`)
 *   so "Toon minder" keeps working even though removing the clamp class
 *   makes a fresh measurement report "no overflow".
 * - 2026-08-30 Codex-finding (PR #1285): a mount-time-only measurement
 *   misses overflow caused by a LATER viewport/orientation/container-width
 *   change or web-font swap -- the user would then have hidden clamped
 *   text with no way to open it. A ResizeObserver on the content element
 *   re-measures on every size change, client-only (created inside the
 *   existing useEffect, so never during SSR) with a disconnect() cleanup.
 *   Environments without ResizeObserver (jsdom by default) fall back to
 *   the single mount-time measurement below.
 * - 2026-08-30 Codex-finding round 2 (PR #1285): ResizeObserver only fires
 *   when the observed BOX itself changes size. A web-font swap can change
 *   line-wrapping (and so introduce new overflow) while the box's width
 *   and clamped two-line height stay identical -- no ResizeObserver
 *   callback follows, so the toggle would stay hidden with real clamped
 *   text underneath. `document.fonts.ready` resolves once the page's
 *   fonts have finished loading/swapping, so the SAME `measure()` also
 *   re-runs there, guarded for environments without `document.fonts`
 *   (jsdom by default) and cancelled on unmount so it never calls
 *   `setState` after teardown; a rejected ready promise is swallowed.
 *
 * `html` must already be sanitized by the caller (see src/lib/sanitize.js)
 * -- this component only lays it out, it does not sanitize.
 */
// Alleen op .de: de pagina heeft al een h1 (de filmkop); een h1 in CMS-tekst zou er
// een tweede van maken. Alleen het tag-niveau verandert, de tekst niet.
function demoteH1(html) {
  return typeof html === "string" ? html.replace(/<(\/?)h1(?=[\s>/])/gi, "<$1h2") : html;
}

export default function ExpandableIntro({ html, className = "", locale: localeProp }) {
  const ctx = useSiteContext();
  const locale = localeProp || ctx.locale;
  const { t } = getTranslations(locale);
  const [expanded, setExpanded] = useState(false);
  const [clamped, setClamped] = useState(false);
  const contentRef = useRef(null);
  const contentId = useId();

  useEffect(() => {
    const el = contentRef.current;
    if (!el) return;

    // Measured while still visually clamped (collapsed), so this reflects
    // real overflow, not an artifact of `expanded` having removed the
    // clamp class. Re-run on every size change (ResizeObserver) AND on
    // every font-load/-swap (document.fonts.ready) below, not just once
    // on mount.
    const measure = () => {
      setClamped(el.scrollHeight > el.clientHeight + 1);
    };

    measure();

    let cancelled = false;

    if (typeof document !== "undefined" && document.fonts?.ready?.then) {
      document.fonts.ready
        .then(() => {
          if (!cancelled) measure();
        })
        .catch(() => {});
    }

    let observer;
    if (typeof ResizeObserver !== "undefined") {
      observer = new ResizeObserver(measure);
      observer.observe(el);
    }

    return () => {
      cancelled = true;
      observer?.disconnect();
    };
  }, [html]);

  if (!html) return null;

  const showToggle = clamped || expanded;

  return (
    <div className={className}>
      <div
        ref={contentRef}
        id={contentId}
        className={`text-sm lg:text-[15px] text-gray-700 leading-relaxed cms-content [&_p]:text-gray-700 [&_a]:text-blue-700 [&_a]:underline ${
          expanded ? "" : "line-clamp-2"
        }`}
        dangerouslySetInnerHTML={{ __html: locale === "de" ? demoteH1(html) : html }}
      />
      {showToggle && (
        <button
          type="button"
          onClick={() => setExpanded((prev) => !prev)}
          aria-expanded={expanded}
          aria-controls={contentId}
          className="mt-1 text-sm font-medium text-blue-700 hover:text-blue-800 hover:underline"
        >
          {expanded ? t("category.show_less", "Toon minder") : t("category.read_more", "Lees meer")}
        </button>
      )}
    </div>
  );
}
